import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    TrendingUp,
    Warehouse,
    Download,
    Loader2,
    AlertCircle,
} from 'lucide-react';

// API and Hooks
import { fetchStores, fetchInventoryValuationForStore } from '../apiHelper';
import { useToast } from '../hooks/useToast';
import TrialModal from '../components/ui/TrialModal';

const InventoryValuationDashboard = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();

    // State
    const [loading, setLoading] = useState(true);
    const [fetchingReport, setFetchingReport] = useState(false);
    const [stores, setStores] = useState([]);
    const [selectedStoreId, setSelectedStoreId] = useState(null);
    const [reportData, setReportData] = useState([]);

    const [showTrialModal, setShowTrialModal] = React.useState(true); // or use logic like localStorage to show once

    // 1. Initial Load: User and Stores
    useEffect(() => {
        const loadInitialData = async () => {
            setLoading(true);
            const lcUser = localStorage.getItem("user");
            if (!lcUser) {
                showToast({ message: "Please log in.", show: true, toastType: 0 });
                navigate('/login');
                return;
            }

            try {
                const fetchedStores = await fetchStores(JSON.parse(lcUser));
                const mappedStores = fetchedStores.map(s => ({ label: s.identifier, value: s.store_id }));
                setStores(mappedStores);
                if (mappedStores.length > 0) setSelectedStoreId(mappedStores[0].value);
            } catch (err) {
                showToast({ message: "Failed to load stores.", show: true, toastType: 2 });
            } finally {
                setLoading(false);
            }
        };
        loadInitialData();
    }, [showToast, navigate]);

    // 2. Fetch Report Data
    useEffect(() => {
        if (!selectedStoreId) return;
        const loadReport = async () => {
            setFetchingReport(true);
            try {
                const data = await fetchInventoryValuationForStore(selectedStoreId);
                setReportData(Array.isArray(data) ? data : []);
            } catch (err) {
                showToast({ message: "Failed to fetch valuation.", show: true, toastType: 2 });
            } finally {
                setFetchingReport(false);
            }
        };
        loadReport();
    }, [selectedStoreId, showToast]);

    const summary = useMemo(() => {
        const totalValue = reportData.reduce((sum, item) => sum + item.total_inventory_value, 0);
        const totalItems = reportData.length;
        const totalStock = reportData.reduce((sum, item) => sum + item.total_on_hand, 0);
        const currentStore = stores.find(s => s.value === selectedStoreId)?.label || "N/A";
        return { totalValue, totalItems, totalStock, currentStore };
    }, [reportData, selectedStoreId, stores]);

    const formatCurrency = (val) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(val);

    const navigateStockHistory = (catentry_id) => {
        if (catentry_id && selectedStoreId) {
            navigate('/reports/stock-history', { state: { catentryId: catentry_id, storeId: selectedStoreId } });
        }
    };

    if (loading) return <div className="p-20 text-center"><Loader2 className="animate-spin inline mr-2" /> Initializing...</div>;

    return (
        <div className="min-h-screen bg-gray-50 p-4 md:p-6 print-root">
            {/* SELF-CONTAINED PRINT STYLES */}
            <style dangerouslySetInnerHTML={{
                __html: `
                @media print {
                    /* Hide everything outside the report */
                    body * { visibility: hidden; }
                    .print-root, .print-root * { visibility: visible; }
                    .print-root { position: absolute; left: 0; top: 0; width: 100%; background: white !important; }
                    
                    /* Utility classes */
                    .no-print { display: none !important; }
                    .print-only { display: block !important; }
                    
                    /* Force Desktop Table view in Print */
                    .desktop-table-container { display: block !important; }
                    .mobile-card-container { display: none !important; }
                    
                    /* Table styling for PDF */
                    table { border-collapse: collapse !important; width: 100% !important; }
                    th { background-color: #f8fafc !important; color: #475569 !important; border: 1px solid #e2e8f0 !important; }
                    td { border: 1px solid #e2e8f0 !important; }
                    
                    /* Remove background colors if browser doesn't support Print Backgrounds */
                    .bg-gray-50 { background-color: white !important; }
                }
                .print-only { display: none; }
            `}} />

            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <Warehouse className="text-blue-600 no-print" />
                        Inventory Valuation Report
                    </h1>
                    <p className="text-gray-500 font-medium">Store: <span className="text-black font-bold">{summary.currentStore}</span></p>
                    <p className="print-only text-[10px] text-gray-400 mt-1">Ref Date: {new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}</p>
                </div>

                <div className="flex items-center gap-2 w-full md:w-auto no-print">
                    <select
                        value={selectedStoreId || ""}
                        onChange={(e) => setSelectedStoreId(Number(e.target.value))}
                        className="flex-1 md:w-64 bg-white border border-gray-300 rounded-lg p-2 text-sm"
                    >
                        {stores.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    <button
                        onClick={() => window.print()}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm hover:bg-blue-700 transition-colors shadow-sm"
                    >
                        <Download size={16} /> <span className="hidden sm:inline">Export PDF</span>
                    </button>
                </div>
            </div>

            {fetchingReport ? (
                <div className="py-20 text-center text-gray-400"><Loader2 className="animate-spin inline mb-2" /><br />Refreshing Data...</div>
            ) : (
                <>
                    {/* Summary Boxes */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                            <span className="text-[10px] text-gray-400 font-bold uppercase block mb-1">Portfolio Value</span>
                            <div className="text-xl font-black">{formatCurrency(summary.totalValue)}</div>
                        </div>
                        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                            <span className="text-[10px] text-gray-400 font-bold uppercase block mb-1">Stock on Hand</span>
                            <div className="text-xl font-black">{summary.totalStock.toLocaleString()}</div>
                        </div>
                        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                            <span className="text-[10px] text-gray-400 font-bold uppercase block mb-1">Unique Products</span>
                            <div className="text-xl font-black">{summary.totalItems}</div>
                        </div>
                    </div>

                    {/* Desktop View / Print View */}
                    <div className="desktop-table-container bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hidden md:block">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200">
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Product Name</th>
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase text-right">Qty</th>
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase text-right">Avg Cost</th>
                                    <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase text-right">Total Value</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {reportData.map((item) => (
                                    <tr
                                        key={item.catentry_id}
                                        onClick={() => navigateStockHistory(item.catentry_id)}
                                        className="hover:bg-blue-50/50 cursor-pointer no-print-pointer"
                                    >
                                        <td className="px-6 py-4 font-medium text-gray-900">{item.productname}</td>
                                        <td className="px-6 py-4 text-right font-mono">{item.total_on_hand.toLocaleString()}</td>
                                        <td className="px-6 py-4 text-right">{formatCurrency(item.avg_unit_cost)}</td>
                                        <td className="px-6 py-4 text-right font-bold text-blue-700">{formatCurrency(item.total_inventory_value)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Mobile View (Hidden in Print) */}
                    <div className="mobile-card-container md:hidden flex flex-col gap-3 no-print">
                        {reportData.map((item) => (
                            <div
                                key={item.catentry_id}
                                onClick={() => navigateStockHistory(item.catentry_id)}
                                className="bg-white p-4 rounded-xl border border-gray-200 active:scale-[0.98] transition-transform"
                            >
                                <h3 className="font-bold text-sm mb-2">{item.productname}</h3>
                                <div className="flex justify-between items-end">
                                    <div className="text-[10px] text-gray-500">
                                        Qty: <span className="text-black font-bold">{item.total_on_hand}</span><br />
                                        Cost: {formatCurrency(item.avg_unit_cost)}
                                    </div>
                                    <div className="text-right">
                                        <p className="text-[9px] text-gray-400 uppercase font-bold">Total Value</p>
                                        <p className="text-blue-700 font-black text-sm">{formatCurrency(item.total_inventory_value)}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
            {/* <TrialModal isOpen={showTrialModal} onRequestClose={() => setShowTrialModal(false)} /> */}
        </div>
    );
};

export default InventoryValuationDashboard;